import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { RootScopeService } from './root-scope.service';
import { Global } from '../global/globalApi';
import * as Constants from '../models/Constants';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import _ from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class CustomerAlertsService {

  expiredAlertsDateRangeForm = new FormGroup({
    start: new FormControl<Date | null>(null, Validators.required),
    end: new FormControl<Date | null>(null, Validators.required ),
  });

  constructor(
    public http: HttpClient,
   public rootScope: RootScopeService ) {

  }
  alertListDataMap: any = {};  // caching  alert data list
  public refreshAlerts = new Subject<boolean>();
  public refreshedClicked: boolean= false ;
  /** 
* deleteEntryCustAlertMap() -- deleting API cached data  alerts list for a customer account tab.
* @author :Amarjeet Kumar
*/
  deleteEntryCustAlertMap(tabId) {
    delete this.alertListDataMap[tabId];
  }


  /**
   * Updates the customer tab data for expired alerts.
   * Resets the expiredAlertsDateRangeForm and updates it with the start and end dates from the active customer tab's viewExpiredAlertData.
   * If the start or end date is available, it patches the values in the expiredAlertsDateRangeForm.
   */
  updateCustTabDataForExpiredAlerts(){
    this.expiredAlertsDateRangeForm.reset();
    const startDate = this.rootScope.activeCustTab.viewExpiredAlertData?.startDate;
    const endDate = this.rootScope.activeCustTab.viewExpiredAlertData?.endDate;
    if(startDate || endDate) {
    this.expiredAlertsDateRangeForm.patchValue({start: startDate, end: endDate});
    }

  }
  
  /**
   * Retrieves the expired alerts as an observable.
   *
   * @param beginData - The begin date for filtering the alerts.
   * @param endData - The end date for filtering the alerts.
   * @returns An observable that emits the expired alerts.
   */
  getExpiredAlertsObservable(beginData:any,endData:any) {
    const url = `${Global.domainUrl}${Global.GET_EXPIRED_ALERTS}`;
    const params = {
      beginDate: beginData, 
      endDate: endData
    };
    const headers =  this.rootScope.getCustAccHeaders(Constants.apiVersions, this.rootScope.activeCustTab.customerSessionId);
    return this.http.get(url, { headers, params });
  }


  
  /**
   * Retrieves expired alerts based on the provided begin and end dates.
   * 
   * @param beginDate - The start date for retrieving expired alerts.
   * @param endDate - The end date for retrieving expired alerts.
   */
  getExpiredAlerts(beginDate, endDate) {
    this.rootScope.activeCustTab.viewExpiredAlertData.viewExpiredAlertApiFailure = false;
    this.rootScope.activeCustTab.viewExpiredAlertData.viewExpiredAlerts = [];
    this.rootScope.activeCustTab.viewExpiredAlertData.viewExpiredAlertTableData = [];
    this.rootScope.activeCustTab.viewExpiredAlertData.selectedRow = {};
    this.rootScope.activeCustTab.viewExpiredAlertData.selectedRowDetailData = {};

    this.getExpiredAlertsObservable(beginDate, endDate).subscribe({
      next: (res: any) => {
        this.rootScope.activeCustTab.viewExpiredAlertData.searchExpiredAlerts = true;
        const viewExpiredAlerts = res?.data;
        if (viewExpiredAlerts) {
          this.rootScope.activeCustTab.viewExpiredAlertData.viewExpiredAlerts = viewExpiredAlerts;
          this.createTableData(viewExpiredAlerts);
        }
      },
      error: (error) => {
        this.rootScope.activeCustTab.viewExpiredAlertData.viewExpiredAlertApiFailure = true;
      }
    })
  }


  /**
   * Creates table data for viewExpiredAlertsData.
   * @param viewExpiredAlertsData - The data for viewExpiredAlerts.
   */
  createTableData(viewExpiredAlertsData: any) {
    this.rootScope.activeCustTab.viewExpiredAlertData.viewExpiredAlertTableData = viewExpiredAlertsData?.map((item) => {
      return {
        title: item.headline,
        author: item.authorName,
        expiration: item.expired,
        runtimeAlertId: item.runtimeAlertId
      }
    }).sort((a, b) => new Date(b.expiration).getTime() - new Date(a.expiration).getTime());
  }

  /**
   * Expires the alert by sending a PUT request to the specified URL.
   * @param expireAlertPayload - The payload containing the data for expiring the alert.
   * @returns A promise that resolves to the response from the server.
   */
  expireAlertObservable(expireAlertPayload:any){
    const url = `${Global.domainUrl}${Global.DELETE_EXPIRED_ALERTS}`;
    const headers =  this.rootScope.getCustAccHeaders(Constants.apiVersions, this.rootScope.activeCustTab.customerSessionId);
    return this.http.put(url, expireAlertPayload, { headers });
    
  }

  /**
   * Expires the alert for the given payload.
   * @param expireAlertPayload - The payload for expiring the alert.
   */
  expireAlertApiCall(expireAlertPayload: any, callback:() => void) {
    this.rootScope.activeCustTab.deleteExpireAlertApiFailure = false;

    this.expireAlertObservable(expireAlertPayload).subscribe({
      next: (res: any) => {
      this.updateAlertData();
        callback();
      },
      error: (error) => {
        this.rootScope.activeCustTab.deleteExpireAlertApiFailure = true;
      }
    })

  }  


   /**
   * Handles the expiration alert for approved exceptions.
   * 
   * This method retrieves the selected sub-exception data based on the active customer tab's approved exception types.
   * It then checks if the exception subtype is a security override approved exception. If it is, it retrieves the customer type filter data
   * and the exception filter data based on the active customer tab's customer type and selected exception.
   * 
   * Finally, it constructs an expire alert payload object and calls the customerAlertService to handle the expiration alert.
   */
  expireAlert( callback:() => void) {
    const selectedSubExceptionData = this.rootScope?.subExceptionsList?.find(x => x?.exception_subtype_description == this.rootScope?.activeCustTab?.approvedExceptionsTypes?.selectedSubException);
    const exception_subtype = selectedSubExceptionData?.exception_subtype;

    /* istanbul ignore next */
    if (exception_subtype === Constants.SECURITY_OVERRIDE_APPROVED_EXCEPTION) {
      const customerTypeFilterData = this.rootScope?.activeCustTab?.approvedExceptionsTypes
        ?.find((x: any) => x.exceptionsData?.customerType == this.rootScope?.activeCustTab?.customerType);

      if (customerTypeFilterData.exceptionsData?.exceptions) {
        const exceptionFilterData = customerTypeFilterData?.exceptionsData?.exceptions
          ?.find((x: any) => x?.exception_description == this.rootScope?.activeCustTab?.approvedExceptionsTypes?.selectedException);
      
          const exception_type = exceptionFilterData?.exception_type;

        if (exception_type) {
          const isSecurityOverride = true;

          const expireAlertPayload = {
            approvedExceptionType: exception_type,
            approvedExceptionSubType: exception_subtype,
            isSecurityOverride: isSecurityOverride
          };

          this.expireAlertApiCall(expireAlertPayload, callback);
        }
      }
    }
  }


  /**
   * Updates the alert data by filtering out objects with a manualAlertSubType of 'Security Override Allowed'.
   */
  updateAlertData(){
    let previousData  =  this.alertListDataMap[this.rootScope.activeCustTab.customerSessionId]?.data; 
    let newData = _.filter(previousData, function (currentObject) {
      return currentObject.manualAlertSubType != 'Security Override Allowed';
    })
    this.alertListDataMap[this.rootScope.activeCustTab.customerSessionId].data = newData;
  }


}
